// Centralized notification behavior config: priority, color, CTA, sound.
//
// Priority rules (product-owned):
//   HIGH   = anything consultation-related: queue join/pause/resume/called,
//            consultation request/accept/start/end, prescription, lab,
//            async chat message received, follow-up, doctor availability,
//            appointment reminders. HIGH always plays sound & vibrates —
//            the user CANNOT mute HIGH from settings (safety override).
//   LOW    = marketing / system-led notifications (broadcast, promo).
//   MEDIUM = everything else transactional (refund, pharmacy update,
//            social feed events downgraded to LOW).

export type NotificationPriority = "critical" | "high" | "medium" | "low";

export interface NotificationBehavior {
  priority: NotificationPriority;
  dotClass: string;
  ctaLabel?: string;
  alert?: boolean;
}

export const NOTIFICATION_BEHAVIOR: Record<string, NotificationBehavior> = {
  // ---- HIGH (consultation-related, always alerts, cannot be muted) ----
  queue_called: { priority: "critical", dotClass: "bg-destructive", ctaLabel: "Join Now", alert: true },
  consultation_request: { priority: "high", dotClass: "bg-destructive", ctaLabel: "Open Request", alert: true },
  consultation_accepted: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open", alert: true },
  consultation_started: { priority: "high", dotClass: "bg-destructive", ctaLabel: "Open", alert: true },
  consultation_ended: { priority: "high", dotClass: "bg-warning", ctaLabel: "View", alert: true },
  queue_joined: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open Queue", alert: true },
  queue_paused: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open", alert: true },
  queue_resumed: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open", alert: true },
  prescription: { priority: "high", dotClass: "bg-warning", ctaLabel: "View Prescription", alert: true },
  follow_up: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open", alert: true },
  lab_result_ready: { priority: "high", dotClass: "bg-warning", ctaLabel: "View Result", alert: true },
  lab_update: { priority: "high", dotClass: "bg-warning", ctaLabel: "View Lab", alert: true },
  doctor_available: { priority: "high", dotClass: "bg-warning", ctaLabel: "Book Now", alert: true },
  appointment_reminder: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open Appointment", alert: true },
  appointment_started: { priority: "high", dotClass: "bg-warning", ctaLabel: "Open", alert: true },
  appointment_missed: { priority: "high", dotClass: "bg-warning", ctaLabel: "View", alert: true },
  appointment_booked: { priority: "high", dotClass: "bg-warning", ctaLabel: "View", alert: true },
  async_received: { priority: "high", dotClass: "bg-destructive", ctaLabel: "Reply", alert: true },
  async_replied: { priority: "high", dotClass: "bg-destructive", ctaLabel: "Open", alert: true },

  // ---- MEDIUM (transactional, mutable) ----
  refund_processed: { priority: "medium", dotClass: "bg-primary", ctaLabel: "View Wallet", alert: true },
  pharmacy_update: { priority: "medium", dotClass: "bg-primary", ctaLabel: "View Order", alert: true },
  credit_purchase: { priority: "medium", dotClass: "bg-primary", ctaLabel: "View Wallet", alert: true },
  new_review: { priority: "medium", dotClass: "bg-primary", ctaLabel: "View", alert: true },

  // ---- LOW (marketing / system / social) ----
  broadcast: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  promo: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  marketing: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  system: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  feed_like: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  feed_comment: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  post_like: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  post_reply: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
  doctor_new_follower: { priority: "low", dotClass: "bg-muted-foreground", alert: true },
};

export function getBehavior(type: string): NotificationBehavior {
  return NOTIFICATION_BEHAVIOR[type] ?? { priority: "medium", dotClass: "bg-primary", alert: true };
}

/** Local user preference cache — filled by useNotificationBehavior on mount. */
export interface LocalNotificationPrefs {
  mute_sound: boolean;
  mute_medium: boolean;
  mute_low_priority: boolean;
}

let cachedPrefs: LocalNotificationPrefs = {
  mute_sound: false,
  mute_medium: false,
  mute_low_priority: false,
};

export function setLocalPrefs(p: Partial<LocalNotificationPrefs>) {
  cachedPrefs = { ...cachedPrefs, ...p };
}
export function getLocalPrefs(): LocalNotificationPrefs {
  return cachedPrefs;
}

/**
 * HIGH/critical always plays sound. For medium/low the user can mute
 * (either globally via mute_sound, or per-priority).
 */
export function shouldPlaySound(priority: NotificationPriority): boolean {
  if (priority === "critical" || priority === "high") return true;
  if (cachedPrefs.mute_sound) return false;
  if (priority === "medium" && cachedPrefs.mute_medium) return false;
  if (priority === "low" && cachedPrefs.mute_low_priority) return false;
  return true;
}

// ---------- Sound / vibration primitives ----------
//
// Browsers block audio until the user has interacted with the page: a freshly
// constructed AudioContext starts in state "suspended", and anything scheduled
// on it is silent. The previous implementation built a NEW context per alert,
// so every notification that arrived before a user gesture played nothing —
// and contexts leaked on each call. We now keep ONE shared context and resume
// it on the first user gesture (see unlockAudio, wired up in App).

let sharedCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  try {
    const AC = (window.AudioContext || (window as any).webkitAudioContext);
    if (!AC) return null;
    if (!sharedCtx) sharedCtx = new AC();
    // Autoplay policy can re-suspend the context (e.g. after tab is hidden).
    if (sharedCtx.state === "suspended") void sharedCtx.resume().catch(() => {});
    return sharedCtx;
  } catch {
    return null;
  }
}

/** True once the audio context is running and alert sounds will be audible. */
export function isAudioUnlocked(): boolean {
  return sharedCtx?.state === "running";
}

/**
 * Resume the shared AudioContext. MUST be called from within a real user
 * gesture handler (click/keydown/touchstart) — that is the only moment the
 * browser will honour it. Safe to call repeatedly.
 */
export function unlockAudio(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx || ctx.state !== "suspended") return;
    void ctx.resume().catch(() => {});
  } catch { /* no-op */ }
}

export function playAlertSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1320, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
    osc.start();
    osc.stop(ctx.currentTime + 0.55);
  } catch { /* no-op */ }
}

export function vibrate(pattern: number | number[] = [120, 60, 120]) {
  try {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(pattern);
    }
  } catch { /* no-op */ }
}

export function vibrateUrgent() {
  vibrate([400, 120, 400, 120, 400, 120, 400]);
}

export function playUrgentSound() {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const now = ctx.currentTime;
    for (let i = 0; i < 3; i++) {
      const t = now + i * 0.35;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, t);
      osc.frequency.exponentialRampToValueAtTime(1760, t + 0.25);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.3, t + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.3);
      osc.start(t);
      osc.stop(t + 0.32);
    }
    // NB: never close() — the context is shared and reused for every
    // subsequent alert. Closing it would permanently silence the app.
  } catch { /* no-op */ }
}
